import { Box, Input, Timer, Text, TextProps } from '@betty/moonlight';
import { memo, useCallback, useMemo, useState } from 'react';
import { useSelector } from '@betty/toolkit/state';
import { selectTimeoutEndDate } from '../../../../state';
import { BottomSheet } from '@betty/navigation';
import {
  SELF_EXCLUDE_OPTIONS,
  TIMEOUT_OPTIONS,
} from './PeriodSelect.constants';
import { PeriodSelectOption, PeriodSelectProps } from './PeriodSelect.types';
import { Trans, useTranslation } from '@betty/i18n';
import { Pressable } from 'react-native';

function PeriodSelect({ type, disabled, setValue }: PeriodSelectProps) {
  const { t } = useTranslation('responsibleGaming');
  const timeoutEndDate = useSelector(selectTimeoutEndDate);

  const optionsValue =
    type === 'Timeout' ? TIMEOUT_OPTIONS : SELF_EXCLUDE_OPTIONS;

  const [updatedValues, setUpdatedValues] = useState<string | null>('');

  const handleSelectValue = useCallback(
    (value: PeriodSelectOption) => {
      if (!value) {
        setUpdatedValues(null);
        setValue(null);
      } else {
        setUpdatedValues(value.text);
        setValue(value.dynamicValues);
      }
    },
    [setValue],
  );

  const onResponsibleGamingPress = useCallback(() => {
    BottomSheet.open({
      namespace: 'common',
      type: 'Select',
      data: {
        selectableOptions: Object.keys(optionsValue).map((item) => ({
          text: t(optionsValue[Number(item) as keyof typeof optionsValue]),
          dynamicValues: Number(item),
        })),
        headerNS: 'responsibleGaming',
        headerKey: `select${type}Length`,
        buttonNS: 'common',
        buttonKey: 'select',
        centerContent: true,
        defaultValue: { text: updatedValues },
        maxHeight: '50%',
        onSelect: (v: PeriodSelectOption) => {
          handleSelectValue(v);
        },
      },
    });
  }, [optionsValue, t, type, updatedValues, handleSelectValue]);

  const transComponents = useMemo(
    () => ({
      time: (
        <Timer
          color="darkSurfaceMuted"
          variant="caption-semi"
          time={timeoutEndDate ?? ''}
        />
      ),
    }),
    [timeoutEndDate],
  );

  return (
    <Box gap="space-200" marginTop="space-400">
      <Box gap="space-200" marginBottom="space-300">
        <Pressable onPress={onResponsibleGamingPress}>
          <Input
            value={updatedValues ?? ''}
            placeholderNS="responsibleGaming"
            placeholderKey={`select${type}Length` as TextProps['locKey']}
            editable={false}
            disabled={disabled}
            onPressIn={onResponsibleGamingPress}
          />
        </Pressable>
        {disabled ? (
          timeoutEndDate ? (
            <Text variant="caption" color="darkSurfaceMuted">
              <Trans
                ns="responsibleGaming"
                i18nKey="timeoutExpireTimeText"
                components={transComponents}
              />
            </Text>
          ) : (
            <Text
              variant="caption"
              color="darkSurfaceMuted"
              locNS="responsibleGaming"
              locKey="timeoutExpireText"
            />
          )
        ) : null}
      </Box>
    </Box>
  );
}

export default memo(PeriodSelect);
